import dayjs from 'dayjs';
import { Workbook } from 'exceljs';
import { CalculateMatrixResponse, MatrixContractSettlement, MatrixStripType } from '../../../../Generated/Mark-It-View-Api';
import { MatrixContractSettlementLabels, MatrixStripTypeLabels } from './MatrixLabels';
import { MatrixTermResult } from './MatrixTypes';

interface ExportMatrixParams {
  response: CalculateMatrixResponse;
  productName: string;
  results: MatrixTermResult[];
  weighted: boolean;
  contractSettlement: MatrixContractSettlement;
  innerRange: number;
  outerRange: number;
  innerWeight: number;
  outerWeight: number;
  stripType: MatrixStripType;
  yearsBack: number;
}

function getPercentileValue(deciles: { rank: number; value: number }[], rank: number): number {
  const match = deciles.find(d => Math.abs(d.rank - rank) < 0.001);
  return match?.value ?? 0;
}

export async function exportMatrixToExcel(params: ExportMatrixParams): Promise<void> {
  const { response, productName, results, weighted, contractSettlement, innerRange, outerRange, innerWeight, outerWeight, stripType, yearsBack } = params;

  const workbook = new Workbook();
  const numFmt = '#,##0.0000';

  const matrixBands = [
    { label: '90% - MAX', low: 0.9, high: 1.0 },
    { label: '80% - 90%', low: 0.8, high: 0.9 },
    { label: '70% - 80%', low: 0.7, high: 0.8 },
    { label: '60% - 70%', low: 0.6, high: 0.7 },
    { label: '50% - 60%', low: 0.5, high: 0.6 },
    { label: '40% - 50%', low: 0.4, high: 0.5 },
    { label: '30% - 40%', low: 0.3, high: 0.4 },
    { label: '20% - 30%', low: 0.2, high: 0.3 },
    { label: '10% - 20%', low: 0.1, high: 0.2 },
    { label: 'Min - 10%', low: 0.0, high: 0.1 },
  ];

  // --- Summary Sheet ---
  const summarySheet = workbook.addWorksheet('Summary');

  const headerRow: any[] = ['Band'];
  for (const result of results) {
    headerRow.push(result.season, '');
  }
  summarySheet.addRow(headerRow);

  const subHeaderRow: any[] = [''];
  for (const _result of results) {
    subHeaderRow.push('Low', 'High');
  }
  summarySheet.addRow(subHeaderRow);

  const meanRow: any[] = ['Mean'];
  for (const result of results) {
    meanRow.push(result.weightedMean, '');
  }
  summarySheet.addRow(meanRow);

  const medianRow: any[] = ['Median'];
  for (const result of results) {
    medianRow.push(getPercentileValue(result.weightedDeciles, 0.5), '');
  }
  summarySheet.addRow(medianRow);

  for (const band of matrixBands) {
    const row: any[] = [band.label];
    for (const result of results) {
      row.push(getPercentileValue(result.weightedDeciles, band.low), getPercentileValue(result.weightedDeciles, band.high));
    }
    summarySheet.addRow(row);
  }

  const countRow: any[] = ['Price Count'];
  for (const result of results) {
    if (weighted) {
      countRow.push(`${result.innerPriceCount} / ${result.outerPriceCount}`, '');
    } else {
      countRow.push(result.outerPriceCount, '');
    }
  }
  summarySheet.addRow(countRow);

  // Apply number format to all numeric cells in summary
  summarySheet.eachRow((row) => {
    row.eachCell((cell, colNumber) => {
      if (colNumber > 1 && typeof cell.value === 'number') {
        cell.numFmt = numFmt;
      }
    });
  });

  // --- Per-term Matrix Detail Sheets ---
  for (const result of results) {
    const sheetName = weighted
      ? `Deciles ${result.season} ${innerWeight}|${outerWeight}`
      : `Deciles ${result.season}`;
    const decileSheet = workbook.addWorksheet(sheetName.substring(0, 31));

    if (weighted) {
      decileSheet.addRow(['Rank', 'Inner Decile', 'Outer Decile', 'Weighted', '', '', 'Date', dayjs(response.startDate).format('MM/DD/YYYY')]);

      const ranks = result.weightedDeciles.map(d => d.rank).sort((a, b) => b - a);
      const metadata = [
        ['PPI Applied', response.applyPpi ? 'Yes' : 'No'],
        ['Settlement', MatrixContractSettlementLabels[contractSettlement]],
        ['Inner Range', `${innerRange} Year${innerRange > 1 ? 's' : ''}`],
        ['Outer Range', `${outerRange} Year${outerRange > 1 ? 's' : ''}`],
        ['Inner Weight', `${innerWeight}%`],
        ['Outer Weight', `${outerWeight}%`],
        ['Strip Type', MatrixStripTypeLabels[stripType]],
        ['Above Median', response.aboveMedian ? 'Yes' : 'No'],
      ];

      for (let i = 0; i < ranks.length; i++) {
        const rank = ranks[i];
        const row: any[] = [
          rank,
          getPercentileValue(result.innerDeciles, rank),
          getPercentileValue(result.outerDeciles, rank),
          getPercentileValue(result.weightedDeciles, rank),
          '',
          '',
        ];
        if (i < metadata.length) {
          row.push(metadata[i][0], metadata[i][1]);
        }
        decileSheet.addRow(row);
      }

      decileSheet.addRow([]);
      decileSheet.addRow(['MEAN', result.innerMean, result.outerMean, result.weightedMean]);
      decileSheet.addRow(['MEDIAN',
        getPercentileValue(result.innerDeciles, 0.5),
        getPercentileValue(result.outerDeciles, 0.5),
        getPercentileValue(result.weightedDeciles, 0.5),
      ]);
    } else {
      decileSheet.addRow(['Rank', 'Value', '', '', 'Date', dayjs(response.startDate).format('MM/DD/YYYY')]);

      const ranks = result.weightedDeciles.map(d => d.rank).sort((a, b) => b - a);
      const metadata = [
        ['PPI Applied', response.applyPpi ? 'Yes' : 'No'],
        ['Settlement', MatrixContractSettlementLabels[contractSettlement]],
        ['Years Back', `${yearsBack}`],
        ['Strip Type', MatrixStripTypeLabels[stripType]],
        ['Above Median', response.aboveMedian ? 'Yes' : 'No'],
      ];

      for (let i = 0; i < ranks.length; i++) {
        const rank = ranks[i];
        const row: any[] = [rank, getPercentileValue(result.weightedDeciles, rank), '', ''];
        if (i < metadata.length) {
          row.push(metadata[i][0], metadata[i][1]);
        }
        decileSheet.addRow(row);
      }

      decileSheet.addRow([]);
      decileSheet.addRow(['MEAN', result.weightedMean]);
      decileSheet.addRow(['MEDIAN', getPercentileValue(result.weightedDeciles, 0.5)]);
    }

    // Apply number format
    decileSheet.eachRow((row, rowNumber) => {
      row.eachCell((cell, colNumber) => {
        if (typeof cell.value === 'number') {
          if (colNumber === 1) {
            cell.numFmt = '0%';
          } else {
            cell.numFmt = numFmt;
          }
        }
      });
    });
  }

  // --- Per-term Price Sheets ---
  for (const result of results) {
    if (weighted) {
      const innerName = `${result.season} ${innerRange}yr Prices`;
      const innerSheet = workbook.addWorksheet(innerName.substring(0, 31));
      innerSheet.addRow(['Quote Date', 'Price']);
      for (const price of result.innerPrices) {
        innerSheet.addRow([dayjs(price.quoteDate).format('YYYY-MM-DD'), price.price]);
      }

      const innerQuoteDates = new Set(result.innerPrices.map(p => p.quoteDate));
      const outerName = `${result.season} ${outerRange}yr Prices`;
      const outerSheet = workbook.addWorksheet(outerName.substring(0, 31));
      outerSheet.addRow(['Quote Date', 'Price']);
      for (const price of result.outerPrices) {
        if (!innerQuoteDates.has(price.quoteDate)) {
          outerSheet.addRow([dayjs(price.quoteDate).format('YYYY-MM-DD'), price.price]);
        }
      }

      // Format price columns
      innerSheet.getColumn(2).numFmt = numFmt;
      outerSheet.getColumn(2).numFmt = numFmt;
    } else {
      const priceName = `${result.season} ${yearsBack}yr Prices`;
      const priceSheet = workbook.addWorksheet(priceName.substring(0, 31));
      priceSheet.addRow(['Quote Date', 'Price']);
      for (const price of result.outerPrices) {
        priceSheet.addRow([dayjs(price.quoteDate).format('YYYY-MM-DD'), price.price]);
      }
      priceSheet.getColumn(2).numFmt = numFmt;
    }
  }

  const quoteDate = dayjs(response.startDate).format('YYYY-MM-DD');
  const filename = `${productName} ${quoteDate}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
